/**
 * "New" lesson badge (B4-5): lessons added after the learner started a program
 * arrive flagged `isNew` from the BE. The badge clears once the learner opens
 * the lesson, tracked per user + program in localStorage so it survives a
 * refresh without another round trip. Every storage access is guarded: the
 * badge is a convenience, never a reason to break a learner page.
 */
const storageKey = (userId: string, grpId: string) => `dosen.newLessonsSeen.${userId || 'anon'}.${grpId}`;

export const getSeenNewLessons = (userId: string, grpId: string): Set<string> => {
  if (!grpId) return new Set();
  try {
    const raw = localStorage.getItem(storageKey(userId, grpId));
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
  } catch {
    return new Set();
  }
};

export const markNewLessonSeen = (userId: string, grpId: string, taskId: string): Set<string> => {
  const seen = getSeenNewLessons(userId, grpId);
  if (!grpId || !taskId || seen.has(taskId)) return seen;
  seen.add(taskId);
  try {
    localStorage.setItem(storageKey(userId, grpId), JSON.stringify(Array.from(seen)));
  } catch {
    /* storage full or blocked: the badge simply comes back next load */
  }
  return seen;
};

/** Badge rule shared by every surface: flagged by the BE, not complete, not yet opened. */
export const showsNewBadge = (isNew: boolean | undefined, complete: boolean, seen: Set<string>, taskId: string) =>
  Boolean(isNew) && !complete && !seen.has(taskId);
